import styled from '@emotion/styled';
import { Prismic, PrismicLink } from '@landsbankinn/prismic';
import { LayoutContext } from '@landsbankinn/slices/components/contexts/LayoutContext';
import { container, device } from '@landsbankinn/ui';
import Link from 'next/link';
import React, { useContext, useEffect, useRef, useState } from 'react';
import type { SpringConfig } from 'react-spring';
import { animated, useSpring, useTrail } from 'react-spring';
import type { NavItem, NavItemLevel2 } from './Navigation';
import { SecondLevelItem } from './SecondLevelItem';
import { SwooshLink } from './SwooshLink';
import { ThirdLevelItem } from './ThirdLevelItem';

export const Main = styled<any>(animated.div)`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  height: calc(100vh - 80px);
  overflow: hidden;
  z-index: 10;
`;

const Container = styled.div`
  ${container}
  height: 100%;
  position: relative;
`;

const Reset = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;

  @media (min-height: 701px) {
    overflow-y: visible;
    overflow-x: visible;
  }
`;

const Background = styled(animated.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 34px;
  position: relative;
  overflow: visible;

  @media (min-height: 980px) {
    margin-bottom: 64px;
  }

  @media ${device.tablet} {
    flex-direction: row;
  }
`;

const Top = styled(animated.div)`
  flex: 1;
  margin-bottom: 30px;
`;

const BottomRight = styled(animated.div)`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-bottom: 32px;

  @media ${device.tablet} {
    justify-content: flex-end;
    margin-bottom: 0;
  }
`;

const useBoxSpring = (
  from: { [key: string]: any } = { opacity: 0 },
  config?: SpringConfig,
) => {
  const [style, spring] = useSpring(() => ({
    from,
    config,
  }));
  return { style, spring };
};

const SubnavList = styled.ul`
  margin: 0px;
  margin-top: -12px;
  list-style: none;
  overflow-y: auto;
  flex: 1;
`;

const NavContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 56px;
  height: 100%;

  @media (min-height: 960px) {
    padding-top: 112px;
  }
`;

const BottomBackground = styled(animated.div)`
  @media ${device.tablet} {
    background-color: transparent;
    transition: background-color 200ms ease-in-out;

    @media (min-height: 801px) {
      display: block;
      background-color: ${(props) => props.theme.colors.backgroundAlt};
      position: absolute;
      height: 134px;
      left: -100vw;
      right: -100vw;
      top: -34px;
      z-index: 1;
    }

    @media (min-height: 980px) {
      height: 194px;
      top: -64px;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

function Subnav({ items, open, activeIds }: any) {
  const { setIsNavigationOpen } = useContext(LayoutContext);
  const config = { mass: 5, tension: 2000, friction: 200 };
  const trail = useTrail(items.length, {
    config,
    opacity: open ? 1 : 0,
    reverse: !open,
    from: {
      opacity: 0,
    },
  });

  return (
    <SubnavList>
      {trail.map((props, index) => {
        return (
          <PrismicLink
            key={index}
            value={items[index].third_level_link}
            title={items[index].third_level_link_text}
          >
            <ThirdLevelItem
              onClick={() => {
                setIsNavigationOpen(false);
              }}
              style={props}
              active={activeIds.includes(
                items[index].third_level_link?._meta.id,
              )}
            />
          </PrismicLink>
        );
      })}
    </SubnavList>
  );
}

const Nav = ({
  items,
  open,
  children,
  activeIds,
}: {
  items: NavItemLevel2[];
  open: boolean;
  children?: React.ReactNode;
  activeIds: string[];
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const [selected, setSelected] = useState<NavItemLevel2 | null>(null);
  const trail = useTrail(items.length, {
    config: { mass: 10, tension: 2000, friction: 200 },
    opacity: open ? 1 : 0,
    reverse: !open,
    from: {
      opacity: 0,
    },
  });

  const onSelect = (item: any) => {
    setSelected(null);
    if (selected === item) return;
    requestAnimationFrame(() => setSelected((x) => (x === item ? null : item)));
  };

  useEffect(() => {
    // find selected level by parent
    let defaultSelected = items.find((item) => {
      const id = (item.primary.nav_link as any)?._meta.id;
      return activeIds.includes(id);
    });

    // find selected level by children
    if (!defaultSelected) {
      defaultSelected = items.find((item) =>
        item.fields?.find((field) =>
          activeIds.includes((field.third_level_link as any)?._meta?.id),
        ),
      );
    }

    if (defaultSelected) {
      setSelected(defaultSelected);
    } else {
      setSelected(null);
    }
  }, [items]);

  useEffect(() => {
    if (open && ref.current) {
      requestAnimationFrame(() => {
        ref.current?.querySelector('a')?.focus();
      });
    }
  }, [open, items]);

  return (
    <NavContainer>
      <NavList ref={ref}>
        {trail.map((props, index) => {
          return (
            <SecondLevelItem
              key={index}
              item={items[index]}
              selected={selected === items[index]}
              onSelect={onSelect}
              style={props}
            />
          );
        })}
        {children}
      </NavList>
      <Subnav
        activeIds={activeIds}
        items={selected?.fields ?? []}
        open={open}
      />
    </NavContainer>
  );
};

interface SheetProps {
  open: boolean;
  item?: NavItem;
  openingHours: any;
  bottomLinks: any;
  quickLinks: any;
  activeIds: string[];
  cards: any;
}

const bottomLinkColors = ['#FF8316', '#409DB0', '#1E3D5A'];

export function Sheet(props: SheetProps) {
  const { open, item, bottomLinks, activeIds } = props;
  const [visible, setVisible] = useState(false);
  const { setScrollDisabled } = useContext(LayoutContext);

  // disable scroll when open
  useEffect(() => {
    setScrollDisabled(open);
  }, [open]);

  // setup animations
  const [style, spring] = useSpring(() => ({
    config: { mass: 1, tension: 280, friction: 50 },
    onRest: (e: any) => {
      if (parseInt(e.value, 10) < -100) {
        setVisible(false);
      }
    },
    y: '-101%',
  }));

  const bottomLeft = useBoxSpring({ opacity: 0, y: 20 });
  const bottomRight = useBoxSpring({ opacity: 0, y: 20 });
  const cardsContainer = useBoxSpring({ opacity: 0, y: 20 });

  useEffect(() => {
    spring.stop();
    cardsContainer.spring.stop();
    bottomRight.spring.stop();
    bottomLeft.spring.stop();

    if (open) {
      setVisible(true);
      spring.start({ y: '0%' });
      cardsContainer.spring.start({ opacity: 1, y: 0, delay: 200 });
      bottomLeft.spring.start({ opacity: 1, y: 0, delay: 300 });
      bottomRight.spring.start({ opacity: 1, y: 0, delay: 400 });
    } else {
      spring.start({ y: '-101%', delay: 400 });
      bottomRight.spring.start({ opacity: 0, y: -20, delay: 0 });
      bottomLeft.spring.start({ opacity: 0, y: -20, delay: 50 });
      cardsContainer.spring.start({ opacity: 0, y: -20, delay: 100 });
    }
  }, [open]);

  return (
    <Main hidden={!visible}>
      <Background style={style} />
      <Container>
        <Reset>
          <Top style={cardsContainer.style}>
            <Nav
              open={open}
              items={item?.navigation_item?.body ?? []}
              activeIds={activeIds}
            />
          </Top>
          <Bottom>
            <BottomRight style={bottomRight.style}>
              {bottomLinks?.map((bottomLink: any, i: number) => {
                const color = bottomLinkColors[i];
                const text = Prismic.text(bottomLink.bottomlink_title);
                return (
                  <Link
                    href={Prismic.link(bottomLink.bottomlink_link).href}
                    passHref
                    key={`bottom-link-${i}`}
                  >
                    <SwooshLink color={color}>{text}</SwooshLink>
                  </Link>
                );
              })}
            </BottomRight>
            <BottomBackground style={{ opacity: bottomRight.style.opacity }} />
          </Bottom>
        </Reset>
      </Container>
    </Main>
  );
}
